/**
 * One person may be a customer AND a reviewer — never both on one job.
 *
 * This used to say "a reviewer may not also be a customer" and block every
 * reviewer from creating projects, on the argument that a reviewer who owns
 * projects can review work adjacent to their own, generate the artefacts the
 * marketplace assesses, and manufacture volume for their own progression.
 * Those are real risks, and they are answered where they arise — PER JOB:
 *
 *   - a reviewer can never claim, be invited to, or adjudicate their own
 *     project (ReviewService.acceptAssignment, InvitationService.accept,
 *     ReviewService.adjudicate);
 *   - nor a colleague's, by roster (ReviewerCompanyService.sharedCompany);
 *   - nor is a colleague's work shown to them in the pool;
 *   - and level moves only from accepted work judged by the other party.
 *
 * The account-level ban answered the same risk by refusing the founder's
 * own programme: nine real people who are customers on some projects and
 * reviewers on others, and every member who learns the product as a customer
 * before earning standing as a reviewer. It also greeted a member who owned
 * projects with "Reviewer accounts do not start their own projects" the day
 * they were accredited. The per-job rule keeps the integrity; this file keeps
 * the SHAPE (one shared verdict, enforced in the services) so the next
 * restriction has somewhere to live.
 *
 * Operators were always exempt and still are.
 */

const norm = (v) => String(v || '').trim().toLowerCase();

// The roles that DO review work. `reviewer` is the accredited one; the
// worker-track disciplines can be assigned review jobs too, so the same
// conflict applies to them.
export const REVIEWING_ROLES = Object.freeze([
  'reviewer',
  'lead_reviewer'
]);

// Owner-group roles that are allowed to do anything, including provisioning a
// project for somebody else.
const OPERATOR_ROLES = new Set([
  'admin', 'super_admin', 'system', 'manager', 'operations_manager', 'support', 'support_lead'
]);

/**
 * Every role an actor holds — the primary one plus anything assigned.
 *
 * Both are needed: `role` can read `user` while `assignedRoles` carries
 * `reviewer`, which is exactly the shape a promoted customer has. Checking
 * only `role` would let precisely the person this rule is about straight
 * through.
 */
export const rolesOf = (actor) => {
  const all = [actor?.role, ...(Array.isArray(actor?.assignedRoles) ? actor.assignedRoles : [])];
  return all.map(norm).filter(Boolean);
};

export const isOperator = (actor) => rolesOf(actor).some((r) => OPERATOR_ROLES.has(r));

export const isReviewer = (actor) => rolesOf(actor).some((r) => REVIEWING_ROLES.includes(r));

/**
 * May this actor create a new project / start a new interview?
 *
 * @returns {{ allowed: boolean, code?: string, message?: string }}
 */
export const mayCreateProjects = (actor) => {
  if (isOperator(actor)) return { allowed: true };
  // Reviewers included. The conflict is screened per job, not per account —
  // see the header. `isReviewer` stays exported for the places that still
  // need to know (the welcome page shows both journeys to such a person).
  void isReviewer;
  return { allowed: true };
};

/** Throwing form, for services that guard by exception. */
export const assertMayCreateProjects = (actor) => {
  const verdict = mayCreateProjects(actor);
  if (verdict.allowed) return;
  throw { status: 403, code: verdict.code, message: verdict.message };
};

export default mayCreateProjects;
